import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Send, TrendingDown, Users, BarChart3, Plus, Trash2, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { useTranslation } from '../../i18n';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailCampaigns = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [priceDropHistory, setPriceDropHistory] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPriceDropDialog, setShowPriceDropDialog] = useState(false);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(null);

  // Price drop form
  const [priceDropForm, setPriceDropForm] = useState({
    vehicle_id: '',
    old_price: '',
    new_price: ''
  });

  // Campaign form
  const [campaignForm, setCampaignForm] = useState({
    title: '',
    subject: '',
    message: '',
    campaign_type: 'general',
    vehicle_ids: [],
    target_audience: 'all'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, campaignsRes, priceDropRes, vehiclesRes] = await Promise.all([
        axios.get(`${API}/email-campaigns/stats`, { headers }),
        axios.get(`${API}/email-campaigns/campaigns`, { headers }),
        axios.get(`${API}/email-campaigns/price-drop-history`, { headers }),
        axios.get(`${API}/vehicles`)
      ]);

      setStats(statsRes.data);
      setCampaigns(campaignsRes.data);
      setPriceDropHistory(priceDropRes.data);
      setVehicles(vehiclesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load email campaign data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendPriceDrop = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.post(
        `${API}/email-campaigns/price-drop?vehicle_id=${priceDropForm.vehicle_id}&old_price=${priceDropForm.old_price}&new_price=${priceDropForm.new_price}`,
        {},
        { headers }
      );

      toast.success(t('admin.priceDropSentSuccess').replace('{count}', response.data.sent_count));
      setShowPriceDropDialog(false);
      setPriceDropForm({ vehicle_id: '', old_price: '', new_price: '' });
      fetchData();
    } catch (error) {
      console.error('Error sending price drop:', error);
      toast.error(error.response?.data?.detail || t('admin.failedToSend'));
    }
  };

  const handleCreateCampaign = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.post(
        `${API}/email-campaigns/campaigns`,
        campaignForm,
        { headers }
      );

      toast.success(t('admin.campaignCreatedSuccess'));
      setShowCampaignDialog(false);
      setCampaignForm({
        title: '',
        subject: '',
        message: '',
        campaign_type: 'general',
        vehicle_ids: [],
        target_audience: 'all'
      });
      fetchData();
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    }
  };

  const handleSendCampaign = async (campaignId) => {
    try {
      setSendingCampaign(campaignId);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.post(
        `${API}/email-campaigns/campaigns/${campaignId}/send`,
        {},
        { headers }
      );

      toast.success(`Campaign sent to ${response.data.sent_count} users!`);
      fetchData();
    } catch (error) {
      console.error('Error sending campaign:', error);
      toast.error(error.response?.data?.detail || 'Failed to send campaign');
    } finally {
      setSendingCampaign(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--accent-primary)' }}></div>
      </div>
    );
  }

  const selectedVehicle = vehicles.find(v => v.id === priceDropForm.vehicle_id);
  const discount = priceDropForm.old_price && priceDropForm.new_price
    ? ((priceDropForm.old_price - priceDropForm.new_price) / priceDropForm.old_price * 100).toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {t('admin.emailMarketing')}
          </h2>
          <p style={{ color: 'var(--text-secondary)' }} className="mt-1">
            {t('admin.manageCampaigns')}
          </p>
        </div>
        <div className="flex gap-3">
          <Dialog open={showPriceDropDialog} onOpenChange={setShowPriceDropDialog}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2" style={{ background: '#DC2626' }}>
                <TrendingDown className="w-4 h-4" />
                {t('admin.priceDropAlert')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{t('admin.sendPriceDropAlert')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-2">{t('admin.selectVehicle')}</label>
                  <Select
                    value={priceDropForm.vehicle_id}
                    onValueChange={(value) => {
                      const vehicle = vehicles.find(v => v.id === value);
                      setPriceDropForm({
                        vehicle_id: value,
                        old_price: vehicle?.price || '',
                        new_price: ''
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a vehicle" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicles.map(vehicle => (
                        <SelectItem key={vehicle.id} value={vehicle.id}>
                          {vehicle.title} - ${vehicle.price?.toLocaleString()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedVehicle && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                    <div className="flex gap-4 items-center">
                      {selectedVehicle.images?.[0] && (
                        <img
                          src={selectedVehicle.images[0]}
                          alt={selectedVehicle.title}
                          className="w-24 h-24 object-cover rounded"
                        />
                      )}
                      <div>
                        <h3 className="font-semibold">{selectedVehicle.title}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {selectedVehicle.year} • {selectedVehicle.make} • {selectedVehicle.model}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Old Price</label>
                    <Input
                      type="number"
                      placeholder="Current price"
                      value={priceDropForm.old_price}
                      onChange={(e) => setPriceDropForm({ ...priceDropForm, old_price: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">New Price</label>
                    <Input
                      type="number"
                      placeholder="Discounted price"
                      value={priceDropForm.new_price}
                      onChange={(e) => setPriceDropForm({ ...priceDropForm, new_price: e.target.value })}
                    />
                  </div>
                </div>

                {discount > 0 && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-semibold text-green-900 dark:text-green-100">
                          {discount}% Discount
                        </p>
                        <p className="text-sm text-green-700 dark:text-green-300">
                          Save ${(priceDropForm.old_price - priceDropForm.new_price).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <Button 
                  className="w-full" 
                  onClick={handleSendPriceDrop}
                  disabled={!priceDropForm.vehicle_id || !priceDropForm.old_price || !priceDropForm.new_price}
                  style={{ background: '#DC2626' }}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send to All Users
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                New Campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>Create Email Campaign</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Campaign Title</label>
                  <Input
                    placeholder="Internal name for this campaign"
                    value={campaignForm.title}
                    onChange={(e) => setCampaignForm({ ...campaignForm, title: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email Subject</label>
                  <Input
                    placeholder="What users will see in their inbox"
                    value={campaignForm.subject}
                    onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Message</label>
                  <Textarea
                    placeholder="Email content (supports HTML)"
                    rows={6}
                    value={campaignForm.message}
                    onChange={(e) => setCampaignForm({ ...campaignForm, message: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Campaign Type</label>
                    <Select
                      value={campaignForm.campaign_type}
                      onValueChange={(value) => setCampaignForm({ ...campaignForm, campaign_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="promotional">Promotional</SelectItem>
                        <SelectItem value="newsletter">Newsletter</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Target Audience</label>
                    <Select
                      value={campaignForm.target_audience}
                      onValueChange={(value) => setCampaignForm({ ...campaignForm, target_audience: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        <SelectItem value="opted_in">Opted In Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  className="w-full"
                  onClick={handleCreateCampaign}
                  disabled={!campaignForm.title || !campaignForm.subject || !campaignForm.message}
                >
                  Create Campaign
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-6 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900">
                <Mail className="w-6 h-6 text-blue-600 dark:text-blue-300" />
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Total Campaigns</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  {stats.total_campaigns}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-green-100 dark:bg-green-900">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-300" />
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Sent</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  {stats.sent_campaigns}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-purple-100 dark:bg-purple-900">
                <Users className="w-6 h-6 text-purple-600 dark:text-purple-300" />
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Total Users</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  {stats.total_users}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-orange-100 dark:bg-orange-900">
                <BarChart3 className="w-6 h-6 text-orange-600 dark:text-orange-300" />
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Opted In</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  {stats.opted_in_users}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Campaigns List */}
      <div className="rounded-lg p-6" style={{ background: 'var(--bg-primary)' }}>
        <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Email Campaigns
        </h3>
        {campaigns.length === 0 ? (
          <div className="text-center py-12">
            <Mail className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-secondary)', opacity: 0.3 }} />
            <p style={{ color: 'var(--text-secondary)' }}>No campaigns yet. Create your first campaign!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {campaigns.map(campaign => (
              <div key={campaign.id} className="border rounded-lg p-4 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {campaign.title}
                    </h4>
                    <Badge variant={campaign.status === 'sent' ? 'default' : 'secondary'}>
                      {campaign.status}
                    </Badge>
                  </div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {campaign.subject}
                  </p>
                  {campaign.sent_at && (
                    <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                      Sent to {campaign.sent_count} users on {new Date(campaign.sent_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                {campaign.status === 'draft' && (
                  <AlertDialog>
                    <Button
                      size="sm"
                      onClick={() => handleSendCampaign(campaign.id)}
                      disabled={sendingCampaign === campaign.id}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {sendingCampaign === campaign.id ? 'Sending...' : 'Send Now'}
                    </Button>
                  </AlertDialog>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Price Drop History */}
      <div className="rounded-lg p-6" style={{ background: 'var(--bg-primary)' }}>
        <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Price Drop Alert History
        </h3>
        {priceDropHistory.length === 0 ? (
          <div className="text-center py-12">
            <TrendingDown className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-secondary)', opacity: 0.3 }} />
            <p style={{ color: 'var(--text-secondary)' }}>No price drop alerts sent yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {priceDropHistory.slice(0, 10).map(alert => (
              <div key={alert.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                      Vehicle ID: {alert.vehicle_id}
                    </p>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      ${alert.old_price.toLocaleString()} → ${alert.new_price.toLocaleString()} 
                      <span className="ml-2 text-green-600 font-semibold">
                        ({alert.discount_percentage}% off)
                      </span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {alert.sent_to_count} users
                    </p>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {new Date(alert.sent_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailCampaigns;
